import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Tabs, message, Form } from 'antd';
import {
  Lead,
  getPublicPoolLeads,
  getMyLeads,
  createLead,
  updateLead,
  deleteLead,
  getLeadStatistics,
  claimLead,
  releaseLead,
} from '../../services/lead';
import { getAssignableUsers, UserAssignee } from '../../services/user';
import { getCurrentUser, UserInfo } from '../../services/auth';
import LeadDrawer from '../../components/LeadDrawer';
import CreateLeadModal from '../../components/LeadDrawer/CreateLeadModal';
import { logError } from '../../utils/logger';
import LeadStatistics from './components/LeadStatistics';
import LeadFilters from './components/LeadFilters';
import LeadActions from './components/LeadActions';
import LeadListView from './components/LeadListViewEnhanced';
import LeadCardView from './components/LeadCardView';
import LeadGroupView from './components/LeadGroupView';
import { useResponsive } from '../../hooks/useResponsive';

const { TabPane } = Tabs;

const LeadManagement: React.FC = () => {
  const navigate = useNavigate();
  const [urlParams, setUrlParams] = useSearchParams();
  const { isMobile } = useResponsive();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('my-leads');
  const [viewMode, setViewMode] = useState<'list' | 'group'>('list');

  // Public pool leads state
  const [publicPoolLeads, setPublicPoolLeads] = useState<Lead[]>([]);
  const [publicPoolTotal, setPublicPoolTotal] = useState(0);
  const [publicPoolPage, setPublicPoolPage] = useState(1);
  const [publicPoolPageSize, setPublicPoolPageSize] = useState(50);
  const [publicPoolSortField, setPublicPoolSortField] = useState<string | undefined>();
  const [publicPoolSortOrder, setPublicPoolSortOrder] = useState<'ascend' | 'descend' | undefined>();

  // My leads state
  const [myLeads, setMyLeads] = useState<Lead[]>([]);
  const [myLeadsTotal, setMyLeadsTotal] = useState(0);
  const [myLeadsPage, setMyLeadsPage] = useState(1);
  const [myLeadsPageSize, setMyLeadsPageSize] = useState(50);
  const [myLeadsSortField, setMyLeadsSortField] = useState<string | undefined>();
  const [myLeadsSortOrder, setMyLeadsSortOrder] = useState<'ascend' | 'descend' | undefined>();

  // Drawer state
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState<number | null>(null);

  // Create modal state
  const [createModalVisible, setCreateModalVisible] = useState(false);

  // Other state
  const [searchForm] = Form.useForm();
  const [statistics, setStatistics] = useState<any>(null);
  const [statisticsLoading, setStatisticsLoading] = useState(false);
  const [users, setUsers] = useState<UserAssignee[]>([]);
  const [currentUser, setCurrentUser] = useState<UserInfo | null>(null);
  const [searchParams, setSearchParams] = useState<any>({ status: 'public_pool' });

  // Load initial data
  useEffect(() => {
    loadCurrentUser();
    loadUsers();
    loadStatistics();
  }, []);

  // 检查 URL 参数，自动打开详情
  useEffect(() => {
    const openDetailId = urlParams.get('openDetail');
    if (openDetailId) {
      const leadId = parseInt(openDetailId, 10);
      if (!isNaN(leadId)) {
        setSelectedLeadId(leadId);
        setDrawerVisible(true);
        // 清除 URL 参数
        urlParams.delete('openDetail');
        setUrlParams(urlParams);
      }
    }
  }, [urlParams, setUrlParams]);

  useEffect(() => {
    if (activeTab === 'public-pool') {
      loadPublicPoolLeads();
    } else {
      loadMyLeads();
    }
  }, [activeTab, publicPoolPage, publicPoolPageSize, publicPoolSortField, publicPoolSortOrder, myLeadsPage, myLeadsPageSize, myLeadsSortField, myLeadsSortOrder, searchParams]);

  const loadCurrentUser = async () => {
    try {
      const user = await getCurrentUser();
      setCurrentUser(user);
    } catch (error) {
      logError('Failed to load current user', error);
    }
  };

  const loadUsers = async () => {
    try {
      const data = await getAssignableUsers();
      setUsers(data);
    } catch (error) {
      logError('Failed to load users', error);
    }
  };

  const loadStatistics = async () => {
    setStatisticsLoading(true);
    try {
      const data = await getLeadStatistics();
      setStatistics(data);
    } catch (error) {
      logError('Failed to load statistics', error);
    } finally {
      setStatisticsLoading(false);
    }
  };

  const loadPublicPoolLeads = async () => {
    setLoading(true);
    try {
      const params: any = {
        page: publicPoolPage,
        page_size: publicPoolPageSize,
        ...searchParams
      };
      
      // 添加排序参数
      if (publicPoolSortField) {
        params.sort_by = publicPoolSortField;
        params.sort_order = publicPoolSortOrder === 'ascend' ? 'asc' : 'desc';
      }
      
      const data = await getPublicPoolLeads(params);
      setPublicPoolLeads(data.items || []);
      setPublicPoolTotal(data.total || 0);
    } catch (error) {
      logError('Failed to load public pool leads', error);
      message.error('加载公海池线索失败');
    } finally {
      setLoading(false);
    }
  };

  const loadMyLeads = async () => {
    setLoading(true);
    try {
      const params: any = {
        page: myLeadsPage,
        page_size: myLeadsPageSize,
        ...searchParams
      };
      
      // 添加排序参数
      if (myLeadsSortField) {
        params.sort_by = myLeadsSortField;
        params.sort_order = myLeadsSortOrder === 'ascend' ? 'asc' : 'desc';
      }
      
      const data = await getMyLeads(params);
      setMyLeads(data.items || []);
      setMyLeadsTotal(data.total || 0);
    } catch (error) {
      logError('Failed to load my leads', error);
      message.error('加载我的线索失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    const values = searchForm.getFieldsValue();
    setSearchParams(values);
    if (activeTab === 'public-pool') {
      setPublicPoolPage(1);
    } else {
      setMyLeadsPage(1);
    }
  };

  const handleReset = () => {
    searchForm.resetFields();
    setSearchParams({});
    if (activeTab === 'public-pool') {
      setPublicPoolPage(1);
    } else {
      setMyLeadsPage(1);
      // 重置排序
      setMyLeadsSortField(undefined);
      setMyLeadsSortOrder(undefined);
    }
  };

  const handleCreate = () => {
    setCreateModalVisible(true);
  };

  const handleCreateSuccess = () => {
    // 刷新列表和统计数据
    if (activeTab === 'public-pool') {
      loadPublicPoolLeads();
    } else {
      loadMyLeads();
    }
    loadStatistics();
  };

  const handleEdit = (lead: Lead) => {
    setSelectedLeadId(lead.id);
    setDrawerVisible(true);
  };

  const handleView = (lead: Lead) => {
    setSelectedLeadId(lead.id);
    setDrawerVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteLead(id);
      message.success('删除成功');
      if (activeTab === 'public-pool') {
        loadPublicPoolLeads();
      } else {
        loadMyLeads();
      }
      loadStatistics();
    } catch (error) {
      logError('Failed to delete lead', error);
      message.error('删除失败');
    }
  };

  const handleClaim = async (id: number) => {
    try {
      await claimLead(id);
      message.success('领取成功');
      loadPublicPoolLeads();
      loadMyLeads();
      loadStatistics();
    } catch (error) {
      logError('Failed to claim lead', error);
      message.error('领取失败');
    }
  };

  const handleRelease = async (id: number) => {
    try {
      await releaseLead(id);
      message.success('释放成功');
      loadPublicPoolLeads();
      loadMyLeads();
      loadStatistics();
    } catch (error) {
      logError('Failed to release lead', error);
      message.error('释放失败');
    }
  };

  const handleDrawerClose = () => {
    setDrawerVisible(false);
    setSelectedLeadId(null);
  };

  const handleDrawerUpdate = () => {
    // 刷新列表
    if (activeTab === 'public-pool') {
      loadPublicPoolLeads();
    } else {
      loadMyLeads();
    }
    loadStatistics();
  };

  const handleExport = () => {
    message.info('导出功能开发中');
  };

  const handlePageChange = (page: number, pageSize?: number) => {
    if (activeTab === 'public-pool') {
      setPublicPoolPage(page);
      if (pageSize) setPublicPoolPageSize(pageSize);
    } else {
      setMyLeadsPage(page);
      if (pageSize) setMyLeadsPageSize(pageSize);
    }
  };

  const currentLeads = activeTab === 'public-pool' ? publicPoolLeads : myLeads;
  const currentTotal = activeTab === 'public-pool' ? publicPoolTotal : myLeadsTotal;
  const currentPage = activeTab === 'public-pool' ? publicPoolPage : myLeadsPage;
  const currentPageSize = activeTab === 'public-pool' ? publicPoolPageSize : myLeadsPageSize;

  return (
    <div style={{ padding: '24px' }}>
      {/* Statistics */}
      <LeadStatistics statistics={statistics} loading={statisticsLoading} activeTab={activeTab} />

      {/* Main Content */}
      <Card style={{ marginTop: '16px' }}>
        {/* Filters and Actions */}
        <LeadFilters
          form={searchForm}
          onSearch={handleSearch}
          onReset={handleReset}
          onCreateLead={handleCreate}
          loading={loading}
          activeTab={activeTab}
        />

        {/* Tabs */}
        <Tabs activeKey={activeTab} onChange={setActiveTab} style={{ marginTop: '16px' }}>
          <TabPane tab="我的线索" key="my-leads">
            {isMobile ? (
              <LeadCardView
                leads={currentLeads}
                loading={loading}
                total={currentTotal}
                page={currentPage}
                pageSize={currentPageSize}
                onPageChange={handlePageChange}
                onView={handleView}
                onAbandon={(lead) => handleRelease(lead.id)}
                showActions="my-leads"
              />
            ) : viewMode === 'list' ? (
              <LeadListView
                leads={currentLeads}
                loading={loading}
                total={currentTotal}
                page={currentPage}
                pageSize={currentPageSize}
                onPageChange={handlePageChange}
                onView={handleView}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onAbandon={(lead) => handleRelease(lead.id)}
                onRefresh={() => {
                  loadMyLeads();
                  loadStatistics();
                }}
                showActions="my-leads"
                sortField={myLeadsSortField}
                sortOrder={myLeadsSortOrder}
                onSortChange={(field, order) => {
                  setMyLeadsSortField(field);
                  setMyLeadsSortOrder(order);
                }}
              />
            ) : (
              <LeadGroupView
                leads={currentLeads}
                groupBy="status"
                onView={(leadId) => handleView(currentLeads.find(l => l.id === leadId)!)}
                onEdit={handleEdit}
                showActions="my-leads"
              />
            )}
          </TabPane>

          <TabPane tab="公海池" key="public-pool">
            {isMobile ? (
              <LeadCardView
                leads={currentLeads}
                loading={loading}
                total={currentTotal}
                page={currentPage}
                pageSize={currentPageSize}
                onPageChange={handlePageChange}
                onView={handleView}
                showActions="public-pool"
              />
            ) : viewMode === 'list' ? (
              <LeadListView
                leads={currentLeads}
                loading={loading}
                total={currentTotal}
                page={currentPage}
                pageSize={currentPageSize}
                onPageChange={handlePageChange}
                onView={handleView}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onClaim={(lead) => handleClaim(lead.id)}
                onRefresh={() => {
                  loadPublicPoolLeads();
                  loadStatistics();
                }}
                showActions="public-pool"
                sortField={publicPoolSortField}
                sortOrder={publicPoolSortOrder}
                onSortChange={(field, order) => {
                  setPublicPoolSortField(field);
                  setPublicPoolSortOrder(order);
                }}
              />
            ) : (
              <LeadGroupView
                leads={currentLeads}
                groupBy="status"
                onView={(leadId) => handleView(currentLeads.find(l => l.id === leadId)!)}
                onEdit={handleEdit}
                onClaim={(lead) => handleClaim(lead.id)}
                showActions="public-pool"
              />
            )}
          </TabPane>
        </Tabs>
      </Card>

      {/* Lead Drawer */}
      <LeadDrawer
        leadId={selectedLeadId}
        visible={drawerVisible}
        onClose={handleDrawerClose}
        onUpdate={handleDrawerUpdate}
        onSwitchLead={(newLeadId) => {
          // 切换到新的线索
          setSelectedLeadId(newLeadId);
        }}
      />

      {/* Create Lead Modal */}
      <CreateLeadModal
        visible={createModalVisible}
        onClose={() => setCreateModalVisible(false)}
        onSuccess={handleCreateSuccess}
      />
    </div>
  );
};

export default LeadManagement;
